import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  CreatePayInBank,
  FindManyPayInBanks,
  FindOne,
  Option,
  PaginateResult,
  PayInBank,
  UpdatePayInBank,
} from '@univacity/shared/types';
import { ObjectUtil } from '@univacity/shared/util';
import { catchError, map, Observable } from 'rxjs';
import { Configuration } from '../config/configuration';

@Injectable({
  providedIn: 'root',
})
export class PayInBankService {
  private baseUrl: string;

  constructor(private http: HttpClient, private config: Configuration) {
    this.baseUrl = this.config.apiMainUrl + '/payment/pay-in-banks';
  }

  find(query: FindManyPayInBanks): Observable<PaginateResult<PayInBank>> {
    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties(query) as Params,
    });
    return this.http.get<PaginateResult<PayInBank>>(this.baseUrl, {
      params: params,
    });
  }

  count(query: FindManyPayInBanks): Observable<number> {
    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties(query) as Params,
    });
    return this.http.get<number>(`${this.baseUrl}/count`, {
      params: params,
    });
  }

  findOne(
    payInBank: string | PayInBank,
    query?: FindOne
  ): Observable<PayInBank> {
    const id = typeof payInBank === 'string' ? payInBank : payInBank._id;
    const q = { ...query };
    const params = new HttpParams({ fromObject: q as Params });
    return this.http.get<PayInBank>(`${this.baseUrl}/${id}`, { params });
  }

  create(payInBank: CreatePayInBank): Observable<PayInBank> {
    return this.http.post<PayInBank>(this.baseUrl, payInBank);
  }

  delete(payInBankID: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${payInBankID}`);
  }

  update(
    payInBankID: string,
    payInBank: UpdatePayInBank
  ): Observable<PayInBank> {
    return this.http.patch<PayInBank>(
      `${this.baseUrl}/${payInBankID}`,
      payInBank
    );
  }

  autoComplete = (
    search: string,
    page?: number,
    query: FindManyPayInBanks = {}
  ): Observable<{ options: Option[]; nextPage?: number | null }> => {
    const params = ObjectUtil.removeEmptyProperties({
      search,
      page,
      sort: 'currency,1',
      select: ['accountNumber', 'bankName', 'currency', 'beneficiaryName'],
      ...(query || {}),
    });
    return this.http
      .get<
        PaginateResult<PayInBank>
        // Pick<PayInBank, '_id' | 'accountNumber' | 'bankName' | 'currency'>
      >(this.baseUrl, {
        params,
      })
      .pipe(
        map((res) => {
          const options = res.docs.map((c) => ({
            label: `(${c.currency})  ${c.beneficiaryName} ${c.accountNumber} - ${c.bankName}`,
            value: c._id,
          }));
          return { options, nextPage: res.nextPage };
        }),
        catchError(() => [])
      );
  };
}
